import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AxiosError, AxiosResponse } from 'axios';
import { message, notification } from 'antd';
import { useHistory } from 'react-router-dom';
import moment from 'moment-timezone';
import { accountSelector, authSelector } from '../store/auth/selectors';
import {
  logout as signOut,
  setAccount,
  setActiveBrand,
  setActiveBrandId,
  setAuthField,
  setPaymentSubscriptionExpiresAt,
  setUser,
  updateAccountAction,
  updateUser,
  updateUserField
} from '../store/auth/actions';
import { BrandForFilter } from '../utils/type';
import { clearCampaigns } from '../store/campaigns/actions';
import * as T from './requestTypes';
import useApi from './useApi';
import addyConfirm from '../components/confirm';
import useIntegration from './useIntegration';
import { hasFullAccess } from '../utils/helpers/hasFullAccess';
import { UpdateAccountPayload } from '../utils/type/account/updateAccountPayload.type';
import { UserRoles } from '../utils/enums';
import { ErrorsType, useErrors } from '../utils/hooks';
import { Submit2faCodeType } from '../utils/type/2fa/submit2faCode.type';
import { AccountType } from '../utils/type/account/accountType.enum';
import { AuthField } from '../store/auth/types';
import { Resend2faCodeType } from '../utils/type/2fa/resend2faCode.type';

interface iAuth {
  logout: () => void;
  errors: ErrorsType;
  login: (data: T.SignInData, from?: string) => void;
  join: (data: T.JoinData) => void;
  updateProfile: (data: T.UpdateProfileData) => void;
  updateAccountCompanyInfo: (data: T.UpdateAccount, silent?: boolean, cb?: () => void) => void;
  updateAccount: (data: UpdateAccountPayload) => void;
  me: (cb?: () => void) => void;
  getAccount: () => void;
  signUp: (data: T.SignUpData) => void;
  setExpiredSubscriptionDate: (expiredAt: string) => void;
  changePassword: (data: T.ChangePasswordData, cb: () => void) => void;
  forgotPassword: (data: T.ForgotPasswordData) => void;
  resetPassword: (data: T.SaveNewPasswordData) => void;
  confirmEmail: (data: T.ConfirmEmailData) => void;
  busy: boolean;
  createHubSpotTicket: (data: T.HubSpotTicketData, cb: () => void) => void;
  submit2faCode: (payload: Submit2faCodeType) => void;
  resend2faCode: (payload: Resend2faCodeType) => Promise<boolean>;
}

const useAuth = (): iAuth => {
  const dispatch = useDispatch();
  const { clearIntegration } = useIntegration();
  const history = useHistory();
  const auth = useSelector(authSelector);
  const account = useSelector(accountSelector);

  const api = useApi();
  const [busy, setBusy] = useState<boolean>(false);
  const { errors, clearErrors, parseErrors } = useErrors();

  const logout = () => {
    dispatch(clearCampaigns());
    dispatch(signOut());
    clearIntegration();
    history.push('/login');
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('refresh_token_expired_at');
  };

  const handleLoginResponse = (data: any, redirect?: boolean, from?: string) => {
    if (data.twoFactorAuth) {
      logout();
      dispatch(setAuthField(data, AuthField.TWO_FACTOR_AUTH));
      history.push('/two-factor');
      return;
    }

    dispatch(setUser(data));
    localStorage.setItem('refresh_token', data.refresh_token);
    localStorage.setItem('refresh_token_expired_at', data.refresh_token_expired_at);

    if ([UserRoles.ADMIN, UserRoles.SUPER_ADMIN].includes(data.role)) {
      if (redirect) history.push(from ? from : '/admin/campaigns');
      return;
    }

    const hasAccessToAllBrands = hasFullAccess(data);

    let activeBrand;
    let activeBrandId: number = Number(localStorage.getItem('activeBrandId'));
    if (activeBrandId) {
      activeBrand = data.account[hasAccessToAllBrands ? 'brands' : 'brandsForFilter']?.find(
        (item: BrandForFilter) => item.id === activeBrandId
      );
    }

    if (!activeBrand) {
      activeBrand = hasAccessToAllBrands ? data.account.brands[0] : data.brandsForFilter[0];
      activeBrandId = activeBrand?.id;
    }

    if (activeBrandId) {
      dispatch(setActiveBrandId(activeBrandId));
      dispatch(setActiveBrand(activeBrand));
      moment.tz.setDefault(activeBrand.timezone);
    }

    if (redirect) {
      history.push(activeBrandId ? from || '/campaigns' : '/create-brand');
    }
  };

  const login = (data: T.SignInData, from?: string) => {
    clearErrors();
    setBusy(true);
    api
      .signIn(data)
      .then((response: AxiosResponse) => {
        setBusy(false);
        if (response.data.accessToken) {
          localStorage.setItem('token', response.data.accessToken);
        }
        handleLoginResponse(response.data, true, from);
      })
      .catch((error: AxiosError) => {
        setBusy(false);
        parseErrors(error, 'Authorization error');
      });
  };

  const signUp = (data: T.SignUpData) => {
    clearErrors();
    setBusy(true);
    api
      .signUp(data)
      .then((response: AxiosResponse) => {
        setBusy(false);
        localStorage.setItem('token', response.data.accessToken);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        localStorage.setItem('refresh_token_expired_at', response.data.refresh_token_expired_at);
        dispatch(setUser(response.data));
        if ([UserRoles.ADMIN, UserRoles.SUPER_ADMIN].includes(response.data.role)) {
          history.push('/admin/campaigns');
          return;
        }

        const firstBrand = hasFullAccess(response.data)
          ? response.data.account.brands[0]
          : response.data.brandsForFilter[0];

        if (
          !firstBrand?.id &&
          (moment(response.data.account.payment_expires_at).isAfter(moment()) ||
            ['free', 'staff'].includes(response.data.account?.type))
        ) {
          history.push('/create-brand');
        } else {
          if (firstBrand?.id) {
            dispatch(setActiveBrandId(firstBrand?.id));
            dispatch(setActiveBrand(firstBrand));

            history.push('/campaigns');
          } else {
            history.push('/account-expired');
          }
        }
      })
      .catch((error: AxiosError) => {
        setBusy(false);
        parseErrors(error, 'Authorization error');
      });
  };

  const forgotPassword = (data: T.ForgotPasswordData) => {
    setBusy(true);
    api
      .forgotPassword(data)
      .then(() => {
        notification.info({
          message: 'Authorization info',
          description: `Email with instructions was sent to ${data.email}`
        });
        setBusy(false);
      })
      .catch((error: AxiosError) => {
        setBusy(false);
        notification.error({
          message: 'Authorization error',
          description: error.response?.data?.message
        });
      });
  };

  const updateProfile = (data: T.UpdateProfileData) => {
    setBusy(true);
    clearErrors();
    api
      .updateProfile(data)
      .then((response: AxiosResponse) => {
        const correctData = {
          ...response.data.user,
          brandsForFilter: response.data.brandsForFilter
        };
        setBusy(false);
        dispatch(updateUser(correctData));
        message.success('Your profile was successfully updated');
      })
      .catch((error: AxiosError) => {
        setBusy(false);
        parseErrors(error);
      });
  };

  const updateAccount = (data: UpdateAccountPayload) => {
    api.updateAccount(data);
  };

  const updateAccountCompanyInfo = (data: T.UpdateAccount, silent?: boolean, cb?: () => void) => {
    clearErrors();
    setBusy(true);
    api
      .updateAccountCompanyInfo(data)
      .then((response: AxiosResponse) => {
        dispatch(
          updateAccountAction({
            ...response.data,
            payment_customer_id: 'newly_created'
          })
        );
        dispatch(
          updateUserField({
            phone_number: data.phone_number
          })
        );

        setBusy(false);
        message.success('Company information was successfully updated');
        if (data.company_name !== account.company_name && !silent) {
          addyConfirm({
            title: 'One moment..',
            content:
              'Updating Account title will affect on your Zapier integration. Please do not forget to update this field in the integration settings'
          });
        }
        if (cb) cb();
      })
      .catch((error: AxiosError) => {
        setBusy(false);
        parseErrors(error);
      });
  };

  const resetPassword = (data: T.SaveNewPasswordData) => {
    setBusy(true);
    api
      .saveNewPassword(data)
      .then(() => {
        notification.info({
          message: 'Authorization info',
          description: 'Password was updated. Please, login'
        });
        setBusy(false);
        history.push('/login');
      })
      .catch((error: AxiosError) => {
        setBusy(false);
        parseErrors(error, 'Authorization error');
      });
  };

  const me = (cb?: () => void) => {
    api
      .signInByRefreshToken({ refreshToken: auth.refresh_token })
      .then((response: AxiosResponse) => {
        handleLoginResponse(response.data, false);
        if (cb) cb();
      })
      .catch((error: AxiosError) => {
        logout();
        notification.error({
          message: 'Authorization error',
          description: error.response?.data?.message
        });
      });
  };

  const confirmEmail = (data: T.ConfirmEmailData) => {
    clearErrors();
    setBusy(true);
    api
      .confirmEmail(data)
      .then(() => {
        clearErrors();
        setBusy(false);
        notification.success({
          message: 'Authorization info',
          description: 'Email was successfully changed'
        });
      })
      .catch((error: AxiosError) => {
        setBusy(false);
        parseErrors(error, 'Authorization error');
      });
  };

  const changePassword = (data: T.ChangePasswordData, cb: () => void) => {
    clearErrors();
    setBusy(true);
    api
      .changePassword(data)
      .then(() => {
        clearErrors();
        setBusy(false);
        notification.success({
          message: 'Authorization info',
          description: 'Password was successfully changed'
        });
        cb();
      })
      .catch((error: AxiosError) => {
        setBusy(false);
        parseErrors(error, 'Authorization error');
      });
  };

  const join = (data: T.JoinData) => {
    clearErrors();
    setBusy(true);
    api
      .join(data)
      .then(() => {
        notification.success({
          message: 'Authorization info',
          description: 'Check your email to continue'
        });
        setBusy(false);
        history.push('/join?emailSent=true');
      })
      .catch((error: AxiosError) => {
        setBusy(false);
        parseErrors(error, 'Authorization error');
      });
  };

  const setExpiredSubscriptionDate = (expiredAt: string) => {
    dispatch(setPaymentSubscriptionExpiresAt(expiredAt));
  };

  const getAccount = () => {
    api.getAccount().then((response: AxiosResponse) => {
      dispatch(setAccount(response.data));
    });
  };

  const createHubSpotTicket = (data: T.HubSpotTicketData, cb: () => void) => {
    setBusy(true);
    api
      .createHubSpotTicket(data)
      .then(() => {
        setBusy(false);
        addyConfirm({
          title: 'Support ticket has been sent!',
          content:
            'We will get back to you as soon as possible on the e-mail registered on your account. If you need urgent assistance with any of the campaigns which are currently live then please contact your customer success manager directly.',
          icon: 'mail'
        });
        cb();
      })
      .catch(() => {
        setBusy(false);
      });
  };

  const submit2faCode = (payload: Submit2faCodeType) => {
    setBusy(true);
    api
      .submit2faCode(payload)
      .then((response) => {
        localStorage.setItem('token', response.data.accessToken);
        handleLoginResponse(response.data, true);
        setBusy(false);
      })
      .catch((error) => {
        message.warn(error.response?.data?.message);
        setBusy(false);
      });
  };

  const resend2faCode = async (payload: Resend2faCodeType) => {
    let success = true;
    try {
      await api.resend2faCode(payload);
      message.success('New verification code has been sent to your email');
    } catch (e) {
      message.warn('Something went wrong');
      success = false;
    }

    return success;
  };

  return {
    logout,
    setExpiredSubscriptionDate,
    join,
    login,
    me,
    getAccount,
    errors,
    busy,
    updateAccountCompanyInfo,
    changePassword,
    updateProfile,
    resetPassword,
    forgotPassword,
    signUp,
    confirmEmail,
    createHubSpotTicket,
    updateAccount,
    submit2faCode,
    resend2faCode
  };
};

export default useAuth;
